import {
  useQuery,
  useMutation,
  QueryKey,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import {useAuth} from '@workos-inc/authkit-react';
import {rootLogger} from '@/features/development/services/root-logger';
import {getCallerInfo} from '@/helpers/stack-trace';

const logger = rootLogger.getChildLogger('useAuthQuery');

/**
 * Custom hook for authenticated queries that depend on the auth token
 */
export function useAuthQuery<TData, TError = Error>(
  queryKey: QueryKey,
  fetchFunction: (token: string) => Promise<TData>,
  options: Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'queryFn'> = {},
) {
  const {user, isLoading, getAccessToken} = useAuth();

  // Determine if user is authenticated based on user object
  const isAuthenticated = Boolean(user);

  // Log auth state for debugging with caller information and stack trace
  const callerInfo = getCallerInfo();
  logger.debug(`useAuthQuery state:`, {
    stack: callerInfo.stack,
    isAuthenticated,
    isLoading,
    enabled: isAuthenticated && !isLoading && options.enabled !== false,
  });

  return useQuery<TData, TError>({
    queryKey,
    async queryFn() {
      try {
        // Double-check authentication before running the query
        if (!user || isLoading) {
          const callerInfo = getCallerInfo();
          logger.warn(`Auth not ready for query ${String(queryKey[0])}`, {
            caller: callerInfo.caller,
            stack: callerInfo.stack,
          });
          throw new Error('Authentication not ready');
        }

        const token = await getAccessToken();
        if (!token) {
          const callerInfo = getCallerInfo();
          logger.warn(`No token available for query ${String(queryKey[0])}`, {
            caller: callerInfo.caller,
            stack: callerInfo.stack,
          });
          throw new Error('No access token available');
        }

        const callerInfo = getCallerInfo();
        logger.debug(`Executing query ${String(queryKey[0])} with token`, {
          caller: callerInfo.caller,
          stack: callerInfo.stack,
        });
        return await fetchFunction(token);
      } catch (error) {
        const callerInfo = getCallerInfo();
        logger.error(`Error in query ${String(queryKey[0])}:`, error, {
          caller: callerInfo.caller,
          stack: callerInfo.stack,
        });
        throw error;
      }
    },
    // Only run when authenticated
    enabled: isAuthenticated && !isLoading && options.enabled !== false,
    // Don't retry on auth errors
    retry(failureCount, error) {
      if (
        error instanceof Error &&
        (error.message === 'Authentication not ready' ||
          error.message === 'No access token available')
      ) {
        return false;
      }

      return failureCount < 3;
    },
    // Always refetch on mount by default
    refetchOnMount: options.refetchOnMount ?? true,
    ...options,
  });
}

/**
 * Custom hook for authenticated mutations that depend on the auth token
 */
export function useAuthMutation<TData, TVariables, TError = Error>(
  mutationFunction: (token: string, variables: TVariables) => Promise<TData>,
  options: Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> = {},
) {
  const {user, isLoading, getAccessToken} = useAuth();

  // Log auth state for debugging with caller information and stack trace
  const callerInfo = getCallerInfo();
  logger.debug(`useAuthMutation - Auth state:`, {
    caller: callerInfo.caller,
    stack: callerInfo.stack,
    isAuthenticated: Boolean(user),
    isLoading,
  });

  return useMutation<TData, TError, TVariables>({
    async mutationFn(variables: TVariables) {
      try {
        // Check authentication before running the mutation
        if (!user || isLoading) {
          const callerInfo = getCallerInfo();
          logger.warn('Auth not ready for mutation', {
            caller: callerInfo.caller,
            stack: callerInfo.stack,
          });
          throw new Error('Authentication not ready');
        }

        const token = await getAccessToken();
        if (!token) {
          const callerInfo = getCallerInfo();
          logger.warn('No token available for mutation', {
            caller: callerInfo.caller,
            stack: callerInfo.stack,
          });
          throw new Error('No access token available');
        }

        const callerInfo = getCallerInfo();
        logger.debug('Executing mutation with token', {
          caller: callerInfo.caller,
          stack: callerInfo.stack,
        });
        return await mutationFunction(token, variables);
      } catch (error) {
        const callerInfo = getCallerInfo();
        logger.error('Error in mutation:', error, {
          caller: callerInfo.caller,
          stack: callerInfo.stack,
        });
        throw error;
      }
    },
    // Don't retry on auth errors
    retry(failureCount, error) {
      if (
        error instanceof Error &&
        (error.message === 'Authentication not ready' ||
          error.message === 'No access token available')
      ) {
        return false;
      }

      return failureCount < 3;
    },
    ...options,
  });
}
